/** @format */

import { FC, useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { useLocale } from "../i18n/hooks";
import { ReactComponent as DashboardIcon } from "./../../assets/icons/dashboard.svg";
import { ReactComponent as Printer } from "./../../assets/icons/print.svg";
import { Accordion } from "react-bootstrap";
import "./Sidebar.scss";
import { selectShowSidebar } from "./sidebarSlice";
import { useAppSelector } from "../../app/hooks";
import { useTranslation } from "react-i18next";
import SidebarLesson from "./SidebarLesson";
import { selectIsEditable } from "../editable/editableSlice";
import { Lesson, ResourceObject } from "../resources/types";

const Sidebar: FC<{ resource?: ResourceObject }> = ({ resource }) => {
  const locale = useLocale();
  const { t: _ } = useTranslation();
  const { resourceId, userId, lessonId, copy } = useParams();
  const isEditable = useAppSelector(selectIsEditable());
  const showSidebar = useAppSelector(selectShowSidebar());

  const isTeacher = useAppSelector((state) => state.auth.isTeacher);

  const [activeKey, setActiveKey] = useState<string | undefined>();

  useEffect(() => {
    setActiveKey(lessonId ?? "dashboard");
  }, [lessonId]);

  return (
    <div
      role="navigation"
      className={`position-lg-absolute col-12 col-sm-8 col-lg-3 ${
        isEditable || (resource && isTeacher && !userId) ? "isEditable" : ""
      } sidebar p-0 flex-column ${showSidebar ? "d-flex" : "d-none"}`}
      id="sidebar"
    >
      <div className="navWrapper px-3 py-3 bg-white">
        <Accordion
          className="h-100 d-flex flex-column"
          activeKey={activeKey}
          onSelect={(e) => {
            setActiveKey(e === null ? "dashboard" : (e as string));
          }}
          flush
        >
          <Accordion.Item className="bg-white border-0" eventKey="dashboard">
            <div className="d-flex justify-content-between align-items-center">
              <NavLink
                title={_("Dashboard")}
                to={`${lessonId ? "." : ""}`}
                className={`d-flex align-items-center flex-grow-1 p-2 dashboard-header`}
                end
              >
                <DashboardIcon className="dashboard-icon float-left me-2" />
                <span>{_("Dashboard")}</span>
              </NavLink>
              {resourceId && !userId && (
                <a
                  title={_("Print resource")}
                  className="dashboard-item flex-shrink-1 p-2"
                  href={`/${locale}/print/${resourceId}`}
                  aria-label="Print version"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Printer className="dashboard-print m-1" />
                </a>
              )}
              {resourceId !== undefined &&
                userId !== undefined &&
                copy !== undefined && (
                  <a
                    title={_("Print resource")}
                    className="dashboard-item flex-shrink-1 p-2"
                    href={`/u/print/${userId}/${resourceId}/${copy}`}
                    aria-label="Print version"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Printer className="dashboard-print m-1" />
                  </a>
                )}
            </div>
          </Accordion.Item>

          {resource &&
            (resource.lessons.length > 0 ? (
              resource.lessons.map((lesson: Lesson, lessonIndex) => (
                <div key={lessonIndex}>
                  <SidebarLesson
                    key={lessonIndex}
                    lesson={lesson}
                    index={lessonIndex}
                    sidebarVisible={showSidebar}
                  />
                </div>
              ))
            ) : (
              <div className="no-items-message d-flex flex-column flex-grow-1 text-center justify-content-center px-4">
                <h3>{_("This resource has no lessons")}</h3>
                <p>
                  {_(
                    "To add lessons navigate to the 'Dashboard' using the button above or by clicking",
                  )}{" "}
                  <NavLink to={``}>{_("here")}</NavLink>
                </p>
              </div>
            ))}
        </Accordion>
      </div>
      <div className="footer px-3 text-center">
        <p className="mx-auto my-4">
          {_("copyright")} {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default Sidebar;
